// Marketing's "Ready for you": next steps Courage found in the records, each already drafted. One is open at a time,
// with the facts behind it on the left and the draft on the right; the draft is the editor. Approving runs it as you
// (sends or opens the email and logs it, puts the post on the calendar, assigns the leads); skipping keeps it off the
// list, with an undo. When Courage's model is set up, it writes each draft from the facts the first time it's opened.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api";
import { ErrorBox, ago } from "../ui";
import { useLoad } from "../hooks";
import { moves, hrefOf, CHANNEL, type Move, type Draft, type Done } from "./movesApi";
import "./ready.css";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName));
const longDate = (d: string) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
const REWRITES = ["Shorter", "Warmer", "More direct", "More technical"];

function openMail(href: string) {
  const a = document.createElement("a"); a.href = href; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove();
}

/** A textarea that grows with what's in it. */
function Grow(props: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }) {
  const r = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { const el = r.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + 2}px`; } }, [props.value]);
  return <textarea ref={r} rows={4} {...props} />;
}

/** `mine` is My Work's view: only the steps that are the person's own (and the unowned ones, for people in Marketing). */
export function ReadyForYou({ onChanged, mine = false, sub, onMoves, openKey: wanted, active = true }: {
  onChanged?: () => void; mine?: boolean; sub?: string;
  /** Off while hidden (My Work keeps it loaded for the list): no step opens, so nothing is drafted, and no shortcuts. */
  active?: boolean;
  /** The steps still open, whenever they change (My Work lists them with the rest of the person's work). */
  onMoves?: (moves: Move[]) => void;
  /** Open this step (My Work links to one). */
  openKey?: string | null;
}) {
  const q = useLoad(() => moves.list(mine));
  const [openKey, setOpenKey] = useState<string | null>(wanted ?? null);
  useEffect(() => { if (wanted) setOpenKey(wanted); }, [wanted]);
  const [gone, setGone] = useState<Record<string, true>>({});
  const [justDone, setJustDone] = useState<Done[]>([]);
  const [toast, setToast] = useState<{ text: string; undo?: () => void; link?: { to: string; label: string }[] } | null>(null);
  const timer = useRef<number>(0);
  const show = (t: typeof toast) => { setToast(t); window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setToast(null), 9000); };

  const list = (q.data?.moves ?? []).filter((m) => !gone[m.key]);
  useEffect(() => { if (q.data) onMoves?.(list); }, [q.data, list.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = active ? list.find((m) => m.key === openKey) ?? list[0] ?? null : null;
  const done = [...justDone, ...(q.data?.done ?? []).filter((d) => !justDone.some((j) => j.key === d.key))];

  const step = useCallback((dir: 1 | -1) => {
    if (!list.length) return;
    const i = open ? list.findIndex((m) => m.key === open.key) : -1;
    const next = list[Math.min(list.length - 1, Math.max(0, i + dir))];
    setOpenKey(next.key);
    requestAnimationFrame(() => { const el = document.getElementById(`rf-${next.key}`); el?.scrollIntoView({ block: "nearest", behavior: "smooth" }); el?.focus({ preventScroll: true }); });
  }, [list, open]);
  useEffect(() => {
    if (!active) return;
    const on = (e: KeyboardEvent) => {
      if (typing(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "j") { e.preventDefault(); step(1); } else if (e.key === "k") { e.preventDefault(); step(-1); }
    };
    document.addEventListener("keydown", on);
    return () => document.removeEventListener("keydown", on);
  }, [step, active]);

  /** After one is done or skipped, the next one down opens (or the one above, at the end). */
  const advance = (m: Move) => { const i = list.findIndex((x) => x.key === m.key); const next = list[i + 1] ?? list[i - 1]; setOpenKey(next?.key ?? null); };
  const finished = (m: Move, r: { summary: string; records: string[] }) => {
    advance(m);
    setGone((g) => ({ ...g, [m.key]: true }));
    setJustDone((d) => [{ key: m.key, kind: m.kind, at: new Date().toISOString(), title: m.title, summary: r.summary, records: r.records }, ...d]);
    show({ text: r.summary, link: r.records.slice(0, 2).map((n) => ({ to: hrefOf(n), label: `Open ${n}` })) });
    onChanged?.();
  };
  const skipped = async (m: Move) => {
    advance(m);
    setGone((g) => ({ ...g, [m.key]: true }));
    try {
      await moves.skip(m.key);
      show({ text: `Skipped: ${m.title}`, undo: async () => { await moves.restore(m.key).catch(() => {}); setGone((g) => { const n = { ...g }; delete n[m.key]; return n; }); setOpenKey(m.key); setToast(null); } });
    } catch (e) { setGone((g) => { const n = { ...g }; delete n[m.key]; return n; }); show({ text: `Couldn't skip it: ${errText(e)}` }); }
  };

  return (
    <section className="rf" aria-labelledby="rf-h">
      <div className="rf-top">
        <h2 id="rf-h">Ready for you{q.data ? <span className="rf-count">{list.length}</span> : null}</h2>
        <p className="rf-sub">{sub ?? "Courage finds these in your leads, emails, events and Business Central sales, and drafts each one. Nothing goes out until you approve it."}</p>
      </div>
      <ErrorBox error={q.error} />
      {!q.data && !q.error ? (
        <div className="rf-list" aria-busy="true">{[0, 1, 2].map((i) => <div key={i} className="rf-row rf-skel" />)}</div>
      ) : q.data ? (
        list.length ? (
          <div className="rf-list">
            {list.map((m) => m.key === open?.key
              ? <MoveCard key={m.key} m={m} ai={q.data!.ai} onDone={(r) => finished(m, r)} onSkip={() => skipped(m)} />
              : (
                <button key={m.key} id={`rf-${m.key}`} className={`rf-row ch ${CHANNEL[m.draft.channel].cls}`} onClick={() => setOpenKey(m.key)} aria-expanded="false">
                  <span className="ch-dot" aria-hidden="true" />
                  <span className="rf-row-t">{m.title}</span>
                  <span className="rf-row-why">{m.why.split(/(?<=\.)\s/)[0]}</span>
                  <span className="rf-row-when">{m.when}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M4.5 2.5 8 6l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>
                </button>
              ))}
          </div>
        ) : (
          <div className="rf-clear">
            <p className="rf-clear-h">You're caught up.</p>
            <p className="muted">Courage looks again each time you open {mine ? "My Work" : "Marketing"}: new leads, clicks, events coming up and customers who stopped ordering.</p>
          </div>
        )
      ) : null}
      {q.data && (done.length > 0 || q.data.skipped > 0) && (
        <details className="rf-done">
          <summary>Done this week: {done.length}{q.data.skipped ? <span className="muted">, {q.data.skipped} skipped</span> : null}</summary>
          <ul>{done.map((d) => (
            <li key={d.key}>
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
              <span className="t">{d.title}</span>
              <span className="s">{d.summary}{d.records.length ? <> {d.records.slice(0, 3).map((n) => <Link key={n} to={hrefOf(n)}>{n}</Link>)}</> : null}</span>
              <span className="w">{ago(d.at)}</span>
            </li>))}
          </ul>
        </details>
      )}
      {toast && (
        <div className="rf-toast" role="status">
          <span>{toast.text}</span>
          {toast.link?.map((l) => <Link key={l.to} to={l.to}>{l.label}</Link>)}
          {toast.undo && <button className="linkish" onClick={toast.undo}>Undo</button>}
          <button className="rf-x" aria-label="Close" onClick={() => setToast(null)}>×</button>
        </div>
      )}
    </section>
  );
}

function MoveCard({ m, ai, onDone, onSkip }: { m: Move; ai: boolean; onDone: (r: { summary: string; records: string[] }) => void; onSkip: () => void }) {
  const [draft, setDraft] = useState<Draft>(m.draft);
  const [to, setTo] = useState(m.to?.email ?? "");
  const [owners, setOwners] = useState<Record<string, string>>(() => Object.fromEntries((m.assign ?? []).map((a) => [a.lead, a.ownerId ?? ""])));
  const [written, setWritten] = useState(m.written);
  const [writing, setWriting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ask, setAsk] = useState("");
  const [menu, setMenu] = useState(false);
  const dirty = useRef(false);
  const card = useRef<HTMLElement>(null);
  const edit = (p: Partial<Draft>) => { dirty.current = true; setDraft((d) => ({ ...d, ...p })); };
  const ch = CHANNEL[draft.channel];

  const write = useCallback(async (instruction?: string) => {
    setWriting(true); setNote(null); setMenu(false);
    try {
      const r = await moves.write(m.key, { instruction, draft });
      if (instruction || !dirty.current) { setDraft((d) => ({ ...d, ...r.draft, date: d.date })); dirty.current = false; }
      setWritten({ by: "courage", model: r.model }); setAsk("");
    } catch (e) {
      setNote(e instanceof ApiError && e.body?.code === "not_configured" ? null : `Courage couldn't write it just now, so this is the draft from the records. ${errText(e)}`);
    } finally { setWriting(false); }
  }, [m.key, draft]);
  // The first time it's opened, Courage writes it from the facts (once; the draft is kept for everyone after that).
  useEffect(() => { if (ai && m.written.by === "rules" && m.draft.channel !== "tasks") write(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [m.key]);

  const approve = async () => {
    if (busy || writing) return;
    setBusy(true); setError(null);
    try {
      const r = await moves.approve(m.key, { draft, to: to.trim() || undefined, assign: m.assign ? Object.entries(owners).filter(([, o]) => o).map(([lead, ownerId]) => ({ lead, ownerId })) : undefined });
      if (r.mailto) openMail(r.mailto);
      onDone({ summary: r.mailto ? `${r.summary}. Your email app opened with it written.` : r.summary, records: r.records });
    } catch (e) { setError(errText(e)); setBusy(false); }
  };
  const onKey = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); approve(); }
    else if (e.key === "Escape" && typing(e.target)) (e.target as HTMLElement).blur();
    else if (!typing(e.target) && e.key === "s" && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); onSkip(); }
  };

  return (
    <article id={`rf-${m.key}`} ref={card} tabIndex={-1} className={`rf-card ch ${ch.cls}`} onKeyDown={onKey} aria-label={m.title}>
      <header className="rf-head">
        <h3>{m.title}</h3>
        <span className="rf-when">{m.when}</span>
      </header>
      <div className="rf-body">
        <div className="rf-why">
          <p>{m.why}</p>
          {m.quote && <blockquote><p>{m.quote}</p><footer>{m.to?.name}{m.to?.title ? `, ${m.to.title}` : ""}</footer></blockquote>}
          <ul className="rf-ev" aria-label="From these records">
            {m.evidence.map((e, i) => <li key={i}>{e.number ? <Link to={hrefOf(e.number)}><span className="n">{e.number}</span> {e.label}</Link> : <span>{e.label}</span>}</li>)}
          </ul>
          {m.owner?.name && <p className="rf-owner">Owner: {m.owner.name}</p>}
        </div>

        <div className={`rf-draft ${writing ? "is-writing" : ""}`}>
          <div className="rf-sheet">
            <div className="rf-sheet-h">
              <span className="ch-tag"><span className="ch-dot" aria-hidden="true" />{ch.label}</span>
              {draft.channel === "linkedin" && <span className="muted">Company page</span>}
              {draft.channel === "tasks" && m.reach && <span className="muted">{m.reach}</span>}
            </div>

            {draft.channel === "email" && <>
              <label className="rf-line"><span className="k">To</span>
                {m.to?.email ? <span className="v">{m.to.name}{m.to.company && m.to.company !== m.to.name ? `, ${m.to.company}` : ""} <span className="muted">{m.to.email}</span></span>
                  : <input type="email" placeholder={`${m.to?.name ?? "Their"} email address`} value={to} onChange={(e) => setTo(e.target.value)} />}
              </label>
              <label className="rf-line"><span className="k">Subject</span><input value={draft.subject ?? ""} onChange={(e) => edit({ subject: e.target.value })} disabled={writing} /></label>
              <Grow className="rf-text" aria-label="Email" value={draft.body} onChange={(e) => edit({ body: e.target.value })} disabled={writing} />
            </>}

            {draft.channel === "linkedin" && <>
              <Grow className="rf-text" aria-label="Post" value={draft.body} onChange={(e) => edit({ body: e.target.value })} disabled={writing} />
              <label className="rf-line rf-line-b"><span className="k">On the calendar</span>
                <input type="date" value={draft.date ?? ""} onChange={(e) => edit({ date: e.target.value })} />
                <span className="muted">{draft.date ? longDate(draft.date) : ""}</span>
              </label>
            </>}

            {draft.channel === "tasks" && <>
              <table className="rf-assign">
                <thead><tr><th>Lead</th><th>Calls them</th></tr></thead>
                <tbody>{(m.assign ?? []).map((a) => (
                  <tr key={a.lead}>
                    <td><Link to={hrefOf(a.lead)}>{a.name}</Link><span className="sub">{[a.title, a.company].filter(Boolean).join(", ")}{a.rating ? <span className={`rf-rating ${a.rating}`}>{a.rating}</span> : null}</span></td>
                    <td><select aria-label={`Who calls ${a.name}`} value={owners[a.lead] ?? ""} onChange={(e) => setOwners((o) => ({ ...o, [a.lead]: e.target.value }))}>
                      {(m.people ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select></td>
                  </tr>))}
                </tbody>
              </table>
              <label className="rf-line rf-line-b rf-line-top"><span className="k">Note on each call</span></label>
              <Grow className="rf-text rf-text-note" aria-label="Note on each call" value={draft.body} onChange={(e) => edit({ body: e.target.value })} />
            </>}
            {writing && <div className="rf-writing" aria-live="polite"><span className="spin" aria-hidden="true" />Courage is writing this from the records…</div>}
          </div>

          {draft.channel !== "tasks" && (
            <div className="rf-pen">
              <span className="muted">{written.by === "courage" ? "Written by Courage from the records. Edit anything." : "Drafted from the records. Edit anything."}</span>
              {ai && (
                <span className="rf-rewrite">
                  <button className="linkish" aria-expanded={menu} onClick={() => setMenu((x) => !x)} disabled={writing}>Rewrite</button>
                  {menu && (
                    <div className="rf-menu" role="dialog" aria-label="Rewrite">
                      <div className="rf-menu-opts">{REWRITES.map((r) => <button key={r} className="rf-chip" onClick={() => write(r.toLowerCase())}>{r}</button>)}</div>
                      <form onSubmit={(e) => { e.preventDefault(); if (ask.trim()) write(ask.trim()); }}>
                        <input autoFocus placeholder="Or say what to change" value={ask} onChange={(e) => setAsk(e.target.value)} />
                        <button className="btn sm" disabled={!ask.trim()}>Rewrite</button>
                      </form>
                    </div>
                  )}
                </span>
              )}
            </div>
          )}
          {note && <p className="rf-note">{note}</p>}
        </div>
      </div>
      {error && <div className="notify warn" role="alert" style={{ margin: "0 18px 12px" }}><span>{error}</span></div>}
      <footer className="rf-foot">
        <span className="rf-does">{draft.channel === "linkedin" && draft.date ? `Adds the post to the content calendar for ${longDate(draft.date)} as a draft.` : m.action.does}</span>
        <button className="btn" onClick={onSkip} disabled={busy} title="Skip (S)">Skip</button>
        <button className="btn primary" onClick={approve} disabled={busy || writing || (draft.channel === "email" && !to.trim())} title="Ctrl+Enter">{busy ? "Working…" : m.action.label}</button>
      </footer>
    </article>
  );
}
